import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Send, X, Loader2, Paperclip, Headphones, FileText, Image as ImageIcon, FilePlus2 } from 'lucide-react';
import axios from 'axios';
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { toast } from '../../hooks/use-toast';
import useChatSocket from '../../hooks/useChatSocket';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx'];
const MAX_FILE_SIZE_MB = 10;

/**
 * OperationSupportFab
 * --------------------
 * FAB "Question sur cette opération" + slide-over chat inline.
 * Crée/ouvre une conversation type OPERATION liée à l'opération courante.
 * Affiche les messages de l'agent Baobab avec avatar coloré + nom + rôle + statut en ligne.
 */

const ROLE_CONFIG = {
  admin: { label: 'Admin', color: 'bg-purple-100 text-purple-700 border-purple-200', avatar: 'bg-purple-500' },
  commercial: { label: 'Commercial', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', avatar: 'bg-emerald-500' },
  operateur: { label: 'Opérateur', color: 'bg-blue-100 text-blue-700 border-blue-200', avatar: 'bg-blue-500' },
  operator: { label: 'Opérateur', color: 'bg-blue-100 text-blue-700 border-blue-200', avatar: 'bg-blue-500' },
  field_ops: { label: 'Terrain', color: 'bg-amber-100 text-amber-700 border-amber-200', avatar: 'bg-amber-500' },
};

const fmtTime = (d) => {
  if (!d) return '';
  try {
    const date = new Date(d);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch (e) { return ''; }
};

const initialsOf = (name) => {
  if (!name) return '?';
  return name.trim().split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase();
};

const isAgentOnline = (lastSeenAt) => {
  if (!lastSeenAt) return false;
  try {
    return (Date.now() - new Date(lastSeenAt).getTime()) < 5 * 60 * 1000;
  } catch (e) { return false; }
};export const OperationSupportFab = ({ operation }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [loadingConv, setLoadingConv] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    const valid = selected.filter(f => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        toast({ title: 'Fichier rejeté', description: `${f.name} : type non supporté`, variant: 'destructive' });
        return false;
      }
      if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        toast({ title: 'Fichier trop volumineux', description: `${f.name} > ${MAX_FILE_SIZE_MB}MB`, variant: 'destructive' });
        return false;
      }
      return true;
    });
    setFiles(prev => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const opId = operation?.id;
  const opNumber = operation?.operationNumber || `Op #${opId?.slice(0, 8)}`;
  const opRoute = `${(operation?.departure || '—').split(',')[0]} → ${(operation?.arrival || '—').split(',')[0]}`;

  // Open & ensure conversation exists
  const openSupport = async () => {
    setOpen(true);
    if (conv) return;
    setLoadingConv(true);
    try {
      // Try fetch existing conv by operationId via the list endpoint (filtered to client)
      const list = await axios.get(`${API}/conversations?type=OPERATION`);
      const existing = (list.data || []).find(c => c.operationId === opId);
      let convData = existing;
      if (!convData) {
        // Create new OPERATION conversation
        const res = await axios.post(`${API}/conversations`, {
          type: 'OPERATION',
          operationId: opId,
          clientId: user?.id,
          message: `Bonjour, j'ai une question concernant l'opération ${opNumber} (${opRoute}).`,
        });
        convData = res.data;
      }
      setConv(convData);
      await loadMessages(convData.id || convData._id);
    } catch (e) {
      console.error('OperationSupport open error:', e);
      toast({ title: 'Erreur', description: "Impossible d'ouvrir la conversation", variant: 'destructive' });
    } finally {
      setLoadingConv(false);
    }
  };

  const loadMessages = async (convId, silent = false) => {
    if (!convId) return;
    try {
      const res = await axios.get(`${API}/messages?conversation_id=${convId}`);
      const newMsgs = res.data || [];
      setMessages((prev) => {
        if (prev.length !== newMsgs.length) return newMsgs;
        const lp = prev[prev.length - 1], ln = newMsgs[newMsgs.length - 1];
        if (!lp || !ln) return newMsgs;
        return (lp.id === ln.id && (lp.content || '') === (ln.content || '')) ? prev : newMsgs;
      });
    } catch (e) {
      if (!silent) console.error('Load messages error:', e);
    }
  };

  // Poll fallback (15s) + WebSocket pour push instantané
  const convId = conv?.id || conv?._id || null;
  const { lastMessage: wsMsg, sendTyping } = useChatSocket(open ? convId : null, user?.id, user?.role);
  const [typingUserName, setTypingUserName] = useState(null);
  const typingClearTimer = useRef(null);

  useEffect(() => {
    if (!wsMsg || !convId) return;
    if (wsMsg.type === 'new_message' || wsMsg.type === 'conversation_updated') {
      loadMessages(convId, true);
      // Clear typing on new message
      setTypingUserName(null);
    } else if (wsMsg.type === 'user_typing') {
      // Ignore self
      if (wsMsg.user_id === user?.id) return;
      if (wsMsg.is_typing) {
        // Best-effort name : use last known name from messages
        const lastFromUser = [...messages].reverse().find(m => m.senderId === wsMsg.user_id);
        setTypingUserName(lastFromUser?.senderName || 'Support Baobab');
        if (typingClearTimer.current) clearTimeout(typingClearTimer.current);
        typingClearTimer.current = setTimeout(() => setTypingUserName(null), 4000);
      } else {
        setTypingUserName(null);
      }
    }
  }, [wsMsg, convId, messages, user?.id]);

  // Poll fallback léger (15s) au cas où WS échouerait
  useEffect(() => {
    if (!open || !convId) return;
    pollRef.current = setInterval(() => loadMessages(convId, true), 15000);
    return () => clearInterval(pollRef.current);
  }, [open, convId]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if ((!text.trim() && files.length === 0) || !conv || sending) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('content', text.trim() || '(Pièce jointe)');
      fd.append('conversation_id', conv.id || conv._id);
      if (opId) fd.append('operation_id', opId);
      files.forEach(f => fd.append('files', f));
      await axios.post(`${API}/messages`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setText('');
      setFiles([]);
      await loadMessages(conv.id || conv._id, true);
    } catch (e) {
      toast({ title: 'Erreur', description: "Impossible d'envoyer le message", variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* FAB */}
      <button
        onClick={openSupport}
        title="Question sur cette opération"
        className="fixed bottom-6 right-6 z-30 flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105"
        data-testid="op-support-fab"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="hidden sm:inline text-sm font-medium">Contacter le support</span>
      </button>

      <Sheet open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="op-support-panel">
          <VisuallyHidden>
            <SheetTitle>Support Baobab — {opNumber}</SheetTitle>
          </VisuallyHidden>
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-5 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
              <Headphones className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">Support Baobab</p>
              <p className="text-xs text-emerald-100 truncate">{opNumber} · {opRoute}</p>
            </div>
            {conv && (
              <button
                onClick={() => { setOpen(false); navigate(`/nouvelle-demande?from_conv=${conv.id || conv._id}`); }}
                className="text-white/90 hover:text-white px-2.5 py-1.5 rounded-md hover:bg-white/15 flex items-center gap-1.5 text-xs font-medium border border-white/20"
                data-testid="op-support-quote-btn"
                title="Créer une demande de transport pré-remplie avec le contexte"
              >
                <FilePlus2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Devis</span>
              </button>
            )}
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white p-1 rounded hover:bg-white/10" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto bg-slate-50 px-4 py-4 space-y-3" data-testid="op-support-messages">
            {loadingConv && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 text-emerald-600 animate-spin" />
              </div>
            )}

            {!loadingConv && messages.length === 0 && (
              <div className="text-center py-10 text-sm text-slate-500">
                Aucun message pour le moment.
              </div>
            )}

            {!loadingConv && messages.map((msg, idx) => {
              const isMine = msg.senderId === user?.id || msg.senderType === 'client';
              const roleKey = (msg.senderType || '').toLowerCase();
              const role = ROLE_CONFIG[roleKey];
              const senderName = msg.senderName || (isMine ? 'Vous' : 'Support Baobab');
              // Use real senderLastSeenAt (from backend), only on the latest message of each agent
              let isLatestFromThisAgent = false;
              if (!isMine) {
                let lastIdx = -1;
                for (let i = messages.length - 1; i >= 0; i--) {
                  if (messages[i].senderId === msg.senderId) { lastIdx = i; break; }
                }
                isLatestFromThisAgent = idx === lastIdx;
              }
              const online = !isMine && isLatestFromThisAgent && isAgentOnline(msg.senderLastSeenAt);
              return (
                <div key={msg.id} className={cn('flex gap-2.5', isMine ? 'flex-row-reverse' : 'flex-row')}>
                  {!isMine && (
                    <div className="relative shrink-0">
                      <Avatar className={cn('w-8 h-8 text-white text-xs font-semibold', role?.avatar || 'bg-slate-500')}>
                        <AvatarFallback className={cn('text-white text-xs font-semibold', role?.avatar || 'bg-slate-500')}>
                          {initialsOf(senderName)}
                        </AvatarFallback>
                      </Avatar>
                      {online && (
                        <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" title="En ligne" />
                      )}
                    </div>
                  )}
                  <div className={cn('max-w-[78%] flex flex-col', isMine ? 'items-end' : 'items-start')}>
                    {!isMine && (
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-xs font-semibold text-slate-700">{senderName}</span>
                        {role && (
                          <span className={cn('text-[10px] px-1.5 py-0.5 rounded border font-medium', role.color)}>
                            {role.label}
                          </span>
                        )}
                      </div>
                    )}
                    <div className={cn(
                      'px-3.5 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words',
                      isMine
                        ? 'bg-emerald-600 text-white rounded-br-sm'
                        : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm'
                    )}>
                      {msg.content}
                    </div>
                    {msg.attachments?.length > 0 && (
                      <div className="mt-1.5 flex flex-col gap-1 items-start">
                        {msg.attachments.map((att) => {
                          const Icon = att.fileType === 'image' ? ImageIcon : FileText;
                          return (
                            <a
                              key={att.id}
                              href={`${process.env.REACT_APP_BACKEND_URL}${att.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1.5 text-xs text-emerald-700 hover:text-emerald-800 underline"
                            >
                              <Icon className="w-3.5 h-3.5" />
                              <span className="truncate max-w-[180px]">{att.filename}</span>
                            </a>
                          );
                        })}
                      </div>
                    )}
                    <span className={cn('text-[10px] mt-1', isMine ? 'text-slate-400' : 'text-slate-400')}>
                      {fmtTime(msg.createdAt || msg.timestamp)}
                    </span>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Composer */}
          <div className="border-t border-slate-200 bg-white p-3">
            {typingUserName && (
              <div className="flex items-center gap-2 px-1 pb-2 text-xs text-slate-500 italic" data-testid="op-support-typing-indicator">
                <span className="flex gap-0.5">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </span>
                <span>{typingUserName} est en train d'écrire…</span>
              </div>
            )}
            {files.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {files.map((f, idx) => (
                  <div key={idx} className="flex items-center gap-1.5 bg-slate-100 rounded-md px-2 py-1 text-xs">
                    <FileText className="w-3 h-3 text-slate-500" />
                    <span className="truncate max-w-[120px]">{f.name}</span>
                    <button onClick={() => removeFile(idx)} className="text-slate-400 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-slate-400 hover:text-emerald-600 p-2 rounded-md hover:bg-emerald-50 transition-colors shrink-0"
                data-testid="op-support-attach-btn"
                title="Joindre un fichier"
              >
                <Paperclip className="w-4 h-4" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="op-support-file-input"
              />
              <textarea
                value={text}
                onChange={(e) => { setText(e.target.value); sendTyping && sendTyping(); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
                }}
                placeholder="Votre message…"
                rows={1}
                className="flex-1 resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 max-h-32"
                data-testid="op-support-input"
              />
              <Button
                onClick={handleSend}
                disabled={(!text.trim() && files.length === 0) || sending || !conv}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 h-10 w-10 p-0 rounded-lg"
                data-testid="op-support-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default OperationSupportFab;
